import { z } from "zod";
import { PROBLEM_DIFFICULTIES } from "@workspace/gamja-platform/types/difficulty";
import {
  draftError,
  effectiveDraft,
  type QuizDraft,
} from "@/app/my/personal-quiz/quiz-draft";
const unique = <T>(items: T[]) => new Set(items).size === items.length;
export const quizConditionsSchema = z
  .object({
    source: z.enum(["wrong", "saved", "all"]),
    statuses: z
      .array(z.enum(["wrong", "unsure", "graduated", "unattempted"]))
      .min(1)
      .max(4)
      .refine(unique),
    count: z.number().int().min(1).max(20),
    theoryCount: z.number().int().min(0).max(20),
    subjects: z
      .array(
        z.enum(["db", "network-os", "sw-design", "sw-dev", "security-newtech"]),
      )
      .max(5)
      .refine(unique),
    languages: z
      .array(z.enum(["c-language", "java", "python", "sql"]))
      .max(4)
      .refine(unique),
    probabilities: z
      .array(z.enum(["99.9%", "80%", "50%", "20%"]))
      .min(1)
      .max(4)
      .refine(unique),
    difficulties: z
      .array(z.enum(PROBLEM_DIFFICULTIES))
      .min(1)
      .max(5)
      .refine(unique),
    calculationOnly: z.boolean(),
    past: z.enum(["all", "only", "exclude"]),
    years: z
      .array(z.string().regex(/^20\d{2}$/))
      .max(100)
      .refine(unique),
    includeBasic: z.boolean(),
  })
  .strict()
  .refine(
    (v) => v.theoryCount <= v.count,
    "분야 문항 수가 총 문항 수를 초과합니다.",
  )
  .refine((v) => !draftError(v), "필수 조건을 선택해 주세요.")
  .transform((v): QuizDraft => effectiveDraft(v));
export type QuizAnswerState =
  import("@workspace/gamja-platform/lib/random-problems-persistence").ProblemAnswerSessionState;
export interface QuizSnapshot {
  name?: string;
  results?: Record<string, QuizAnswerState>;
  id: string;
  createdAt: string;
  conditions: QuizDraft;
  requestedConditions?: QuizDraft;
  problemIds: string[];
  relaxation: "none" | "72h" | "older-set" | "all-sets";
  excludedCount: number;
}
export type GenerateQuizResult =
  | { ok: true; snapshot: QuizSnapshot }
  | {
      ok: false;
      code: "REDUCED_COUNT_CONFIRMATION";
      message: string;
      proposal: { count: number; theoryCount: number };
    }
  | {
      ok: false;
      code:
        | "INSUFFICIENT_CANDIDATES"
        | "INVALID_CONDITIONS"
        | "UNAUTHORIZED"
        | "UNAVAILABLE";
      message: string;
    };
